'use client';

import { GlowEffect } from '@/components/ui/glow-effect';
import { ArrowRight } from 'lucide-react';
import { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import Link from 'next/link';

interface GlowEffectButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  href?: string;
  colors?: string[];
  mode?: 'rotate' | 'pulse' | 'breathe' | 'colorShift' | 'flowHorizontal' | 'static';
  blur?: 'softest' | 'soft' | 'medium' | 'strong' | 'stronger' | 'strongest' | 'none';
  duration?: number;
  scale?: number;
  showArrow?: boolean;
  className?: string;
}

export function GlowEffectButton({
  children,
  href,
  colors = ['#FF5733', '#33FF57', '#3357FF', '#F1C40F'],
  mode = 'colorShift',
  blur = 'soft',
  duration = 3,
  scale = 0.9,
  showArrow = true,
  className,
  ...props
}: GlowEffectButtonProps) {
  const buttonContent = (
    <>
      <div className="relative z-10 flex items-center gap-1">
        {children}
        {showArrow && <ArrowRight className="h-4 w-4" />}
      </div>
    </>
  );

  // w-fit keeps the glow wrapper the same size as the button.
  // Without it, a block-level parent stretches full column width and the
  // absolute GlowEffect becomes a long blue bar (seen on industry modals).
  const buttonElement = (
    <div className="relative group w-fit">
      <GlowEffect
        colors={colors}
        mode={mode}
        blur={blur}
        duration={duration}
        scale={scale}
      />
      <button
        className={cn(
          "relative inline-flex items-center gap-1 rounded-md bg-zinc-950 px-2.5 py-1.5 text-sm text-zinc-50 outline outline-1 outline-[#fff2f21f]",
          "transition-all duration-300 hover:bg-zinc-900 hover:outline-white/20",
          className
        )}
        {...props}
      >
        {buttonContent}
      </button>
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="inline-block">
        {buttonElement}
      </Link>
    );
  }

  return buttonElement;
} 